import { debounce } from "@tanstack/solid-pacer"
import { keepPreviousData, useQuery } from "@tanstack/solid-query"
import { createFileRoute } from "@tanstack/solid-router"
import { createSolidTable, flexRender, getCoreRowModel, type VisibilityState } from "@tanstack/solid-table"
import ChevronDown from "lucide-solid/icons/chevron-down"
import ChevronLeft from "lucide-solid/icons/chevron-left"
import ChevronRight from "lucide-solid/icons/chevron-right"
import ChevronsLeft from "lucide-solid/icons/chevrons-left"
import ChevronsRight from "lucide-solid/icons/chevrons-right"
import { createSignal, For, Show } from "solid-js"
import z from "zod"
import { Button } from "~/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "~/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table"
import { TextField, TextFieldInput } from "~/components/ui/text-field"
import { orpc } from "~/lib/orpc"
import { columns } from "./-components/columns"

export const Route = createFileRoute("/_authenticated/tablet/$orgId/_tablet/citizens/")({
  validateSearch: z.object({
    pageIndex: z.number().int().nonnegative().catch(0),
    pageSize: z.number().int().nonnegative().catch(10),
    search: z.string().catch("")
  }),
  loaderDeps: ({ search: { pageIndex, pageSize, search } }) => ({ pageIndex, pageSize, search }),
  loader: ({ context, deps }) =>
    context.queryClient.prefetchQuery(
      orpc.tablet.citizens.list.queryOptions({
        input: {
          pagination: {
            pageIndex: deps.pageIndex,
            pageSize: deps.pageSize
          },
          search: deps.search
        }
      })
    ),
  component: RouteComponent
})

function RouteComponent() {
  const [columnVisibility, setColumnVisibility] = createSignal<VisibilityState>({})

  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const [searchText, setSearchText] = createSignal(search().search)

  const debouncedSetSearch = debounce(
    (search: string) => {
      navigate({ search: (prev) => ({ ...prev, search }) })
    },
    {
      wait: 500
    }
  )

  const citizens = useQuery(() =>
    orpc.tablet.citizens.list.queryOptions({
      input: {
        pagination: { pageIndex: search().pageIndex, pageSize: search().pageSize },
        search: search().search
      },
      placeholderData: keepPreviousData
    })
  )

  const table = createSolidTable({
    get data() {
      return citizens.data?.rows || []
    },
    get columns() {
      return columns
    },
    get rowCount() {
      return citizens.data?.rowCount
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (updater) => {
      const update = typeof updater === "function" ? updater(search()) : updater
      navigate({ search: (prev) => ({ ...prev, ...update }) })
    },
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      get pagination() {
        return { pageIndex: search().pageIndex, pageSize: search().pageSize }
      },
      get columnVisibility() {
        return columnVisibility()
      }
    },
    manualPagination: true
  })

  return (
    <div class="p-4">
      <div class="flex justify-between pb-4">
        <div class="flex items-center">
          <TextField
            value={searchText()}
            onChange={(value) => {
              setSearchText(value)
              debouncedSetSearch(value)
            }}
          >
            <TextFieldInput class="h-8 max-w-sm" placeholder="Search..." />
          </TextField>
        </div>
        <div class="flex items-center gap-2">
          <DropdownMenu placement="bottom-end">
            <DropdownMenuTrigger as={Button<"button">} variant="outline" class="h-8">
              Columns <ChevronDown />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <For each={table.getAllColumns().filter((column) => column.getCanHide())}>
                {(column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      class="capitalize"
                      checked={column.getIsVisible()}
                      onChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                }}
              </For>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div class="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader class="bg-muted">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <TableRow>
                  <For each={headerGroup.headers}>
                    {(header) => (
                      <TableHead colSpan={header.colSpan}>
                        <Show when={!header.isPlaceholder}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Show>
                      </TableHead>
                    )}
                  </For>
                </TableRow>
              )}
            </For>
          </TableHeader>
          <TableBody>
            <Show
              when={table.getRowModel().rows?.length}
              fallback={
                <TableRow>
                  <TableCell colSpan={columns.length} class="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              }
            >
              <For each={table.getRowModel().rows}>
                {(row) => (
                  <TableRow data-state={row.getIsSelected() && "selected"}>
                    <For each={row.getVisibleCells()}>
                      {(cell) => (
                        <TableCell class="whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      )}
                    </For>
                  </TableRow>
                )}
              </For>
            </Show>
          </TableBody>
        </Table>
      </div>

      <div class="flex justify-end gap-8 pt-4">
        <Select
          value={table.getState().pagination.pageSize}
          onChange={(value) => {
            if (value) {
              table.setPageSize(value)
            }
          }}
          options={[10, 20, 30, 40, 50]}
          placeholder={table.getState().pagination.pageSize}
          itemComponent={(props) => <SelectItem item={props.item}>{props.item.rawValue}</SelectItem>}
        >
          <SelectTrigger aria-label="Page Size" class="h-8 w-20">
            <SelectValue<string>>{(state) => state.selectedOption()}</SelectValue>
          </SelectTrigger>
          <SelectContent />
        </Select>
        <div class="flex w-fit items-center justify-center font-medium text-sm">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div class="flex items-center gap-2 lg:ml-0">
          <Button
            variant="outline"
            class="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span class="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            class="size-8"
            size="icon"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span class="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            class="size-8"
            size="icon"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span class="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            class="hidden size-8 lg:flex"
            size="icon"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span class="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}
